import React, { Ref, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    Snackbar,
    TextField,
    Typography
} from "@mui/material";
import AccountPickerGroup, { AccountPickerGroupValue } from "../../components/widgets/AccountPicker/AccountPickerGroup";
import ContentsRange, { ContentsRangeValue } from "../../components/widgets/ContentsRange/ContentsRange";
import FormTextField from "../../components/widgets/FormTextField/FormTextField";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRange, DesktopDatePicker, DesktopDateRangePicker } from "@mui/x-date-pickers-pro";
import { FormikProps } from "formik";
import { formatDateString, formatDateToISO, isValidDate, parseDate } from "../../util/date";
import {
    FieldSearchContainerValue,
    FieldSearchRecordSeriesValue,
    FieldSearchSetNameValue, useCalculateRecordSeriesQuery,
    useGetTempBarcodeQuery,
    useSearchContainerFieldQuery,
    useSearchRecordSeriesFieldQuery,
    useSearchSetNameFieldQuery
} from "../../services/grace";
import { useAlertDispatch } from "../../context/AlertContext/AlertContext";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import LookupPicker from "../../components/widgets/LookupPicker/LookupPicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CartonPicker from "../../components/widgets/CartonPicker/CartonPicker";
import { MuiTextFieldProps } from "@mui/x-date-pickers/internals";
import { APIFormDef, APIFormFieldDef } from "../../model/User";
import {InventoryEditItem} from "../../model/Inventory";
//redux
import { useAppSelector } from "../../util/hooks";

export interface DynamicBaseItemFormProps {
    type: "container" | "file" | "tape";
    disabled: boolean;
    formDefinition: APIFormDef;
    formValues: FormData;
    submitButtonRef?: Ref<HTMLButtonElement>;
}

export interface FormData extends InventoryEditItem {
    setNameValue: FieldSearchSetNameValue | null;
    recordSeriesValue: FieldSearchRecordSeriesValue | null;
    containerValue: FieldSearchContainerValue | null;
}

interface FormSnackbarOptions {
    open: boolean;
    message: string;
}

export default function DynamicBaseItemForm(props: DynamicBaseItemFormProps & FormikProps<FormData>): JSX.Element {
    const {
        formDefinition,
        values,
        touched,
        errors,
        handleChange,
        handleSubmit,
        setFieldValue,
        disabled,
        submitButtonRef,
        type
    } = props;

    const handleKeyDown = (evt: React.KeyboardEvent<EventTarget & HTMLInputElement>) => {
        if (evt.code === 'Enter') {
            evt. preventDefault();
        }
    };

    const alertDispatch = useAlertDispatch();

    const statusDate = useMemo(() => formatDateString(values.statusDate), [values.statusDate]);
    const addDate = useMemo(() => formatDateString(values.addDate), [values.addDate]);

    const [formSnackbar, setFormSnackbar] = useState<FormSnackbarOptions>({open: false, message: ""});
    const handleFormSnackbarClose = () => {
        setFormSnackbar({open: false, message: ""});
    }

    const [containerOptions, setContainerOptions] = useState<FieldSearchContainerValue[]>([])
    const [containerSearchText, setContainerSearchText] = useState("");

    const selectedValuesContainerForm = useAppSelector((state) => state?.search.selectedValuesContainerForm);
    const selectedValuesFileForm = useAppSelector((state) => state?.search.selectedValuesFileForm);
    const selectedValuesTapeForm = useAppSelector((state) => state?.search.selectedValuesTapeForm);

    useEffect(() => {
        if (selectedValuesContainerForm && type === "container") {
          setFieldValue("accountLevel1", selectedValuesContainerForm.level1 ?? "");
          setFieldValue("accountLevel2", selectedValuesContainerForm.level2 ?? "");
          setFieldValue("accountLevel3", selectedValuesContainerForm.level3 ?? "");
        }
        if (selectedValuesFileForm && type === "file") {
          setFieldValue("accountLevel1", selectedValuesFileForm.level1 ?? "");
          setFieldValue("accountLevel2", selectedValuesFileForm.level2 ?? "");
          setFieldValue("accountLevel3", selectedValuesFileForm.level3 ?? "");
        }
        if (selectedValuesTapeForm && type === "tape") {
          setFieldValue("accountLevel1", selectedValuesTapeForm.level1 ?? "");
          setFieldValue("accountLevel2", selectedValuesTapeForm.level2 ?? "");
          setFieldValue("accountLevel3", selectedValuesTapeForm.level3 ?? "");
        }
      }, [
        selectedValuesContainerForm,
        selectedValuesFileForm,
        selectedValuesTapeForm,
        setFieldValue,
        type,
      ]);

    const {
        data: tempBarcodeData,
        error: tempBarcodeError,
        isError: tempBarcodeIsError,
        isFetching: tempBarcodeIsFetching,
    } = useGetTempBarcodeQuery(null, { skip: values.id !== "" || values.type !== "file" });

    useEffect(() => {
        if (tempBarcodeIsError) {
            const err = tempBarcodeError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Error retrieving temporary barcode - please try again",
                    }
                });
            }
        } else if (tempBarcodeData !== undefined) {
            setFieldValue("barcode", tempBarcodeData.barcode);
        }
    }, [tempBarcodeData, tempBarcodeIsError, tempBarcodeError, alertDispatch, setFieldValue])

    const {
        data: containerData,
        refetch: containerDataRefetch,
        error: containerError,
        isFetching: containerIsFetching,
        isError: containerIsError,
    } = useSearchContainerFieldQuery(containerSearchText, { skip: false });

    useEffect(() => {
        if (containerIsError) {
            const err = containerError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load container barcode data",
                    }
                });
            }
        }

        if (!containerIsFetching && containerData !== undefined) {
            const options:FieldSearchContainerValue[] = [];

            // Check for duplicates and mark them as such so that they can be
            // rendered differently to avoid warnings. Duplicate barcodes are
            // valid due to the fact that the API abstracts multiple databases,
            // each of which can have the items created in them using legacy
            // tools/interfaces that are database specific and therefore do not
            // prevent the duplication of barcodes etc.
            containerData.forEach((container) => {
                let duplicate = false;
                const match = containerData.some((item) => item.barcode === container.barcode && item.id !== container.id);
                if (match) {
                    duplicate = true;
                }

                options.push({
                    ...container,
                    duplicate: duplicate,
                })
            });

            // Add the current selected value to the list of options
            // to prevent the "no matching option" error
            if (values.containerValue !== null && !containerData.some((item) => item.barcode === values.containerValue?.barcode)) {
                options.unshift(values.containerValue);
            }

            setContainerOptions(options);
        }
    }, [containerData, containerIsError, containerIsFetching, containerError, alertDispatch, values.containerValue])

    // Labels need to be rendered to include altCodes if they exist and to be marked as duplicates if necessary
    const getContainerLabel = (option: FieldSearchContainerValue) => {
        if (option.duplicate) {
            return option.altCode === "" ? `${option.barcode} [${option.id}]` : `${option.barcode} (${option.altCode}) [${option.id}]`
        } else {
            return option.altCode === "" ? option.barcode : `${option.barcode} (${option.altCode})`
        }
    }

    const handleContainerChange = (event: React.SyntheticEvent, value: string | FieldSearchContainerValue | null) => {
        if (value == null) {
            setFieldValue("accountLevel1", "");
            setFieldValue("accountLevel2", "");
            setFieldValue("accountLevel3", "");

            if (containerOptions.length === 1 && containerOptions[0].id === "OPENFILE") {
                if (containerSearchText !== "") {
                    setContainerSearchText("");
                } else {
                    containerDataRefetch();
                }
            }
        }

        try {
            const container = value as FieldSearchContainerValue;

            if (container !== null) {
                if (container.id !== "OPENFILE") {
                    setFieldValue("accountLevel1", container.accountLevel1);
                    setFieldValue("accountLevel2", container.accountLevel2);
                    setFieldValue("accountLevel3", container.accountLevel3);
                } else {
                    // Ensure the "OPENFILE" container option exists to prevent warnings/errors
                    setContainerOptions([container]);
                }
            }
        } catch (e) {
            console.log("ERROR:" + e); // TODO improve handling
        }
        setFieldValue("containerValue", value);
    }

    const handleContainerInputChange = (event: React.SyntheticEvent<Element, Event>, value: string, reason: string) => {
        if (reason !== "reset") {
            setContainerSearchText(value);
        } else {
            setContainerSearchText(""); // Reset the search list so the options aren't empty
        }
    }

    const handleAccountChange = (value: AccountPickerGroupValue) => {
        //if ((values.accountLevel1 !== "" && value.level1 === "") ||
        //    (values.accountLevel1 !== "" && values.accountLevel1 !== value.level1) ||
        //    (values.accountLevel2 !== value.level2) ||
        //    (values.accountLevel3 !== value.level3)) {
        //    if (values.recordSeriesValue !== null) {
        //        setFieldValue("recordSeriesValue", null);
        //        setRecordSeriesOptions([]);
        //        setFieldValue("destroyDate", null);
        //        setFieldValue("permanent", false);
        //        setFormSnackbar({open: true, message: "Record series, destroy date, and permanent flag reset"});
        //    }
        //}

        setFieldValue("accountLevel1", value.level1);
        setFieldValue("accountLevel2", value.level2);
        setFieldValue("accountLevel3", value.level3);
    }

    const [setNameOptions, setSetNameOptions] = useState<FieldSearchSetNameValue[]>([])
    const [setNameSearchText, setSetNameSearchText] = useState("");

    const { // TODO prevent initial load until user types
        data: setNameData,
        error: setNameError,
        isLoading: setNameIsLoading,
        isError: setNameIsError,
    } = useSearchSetNameFieldQuery({
        setName: setNameSearchText,
        accountLevel1: values.accountLevel1,
        accountLevel2: values.accountLevel2,
        accountLevel3: values.accountLevel3,
    }, { skip: values.accountLevel1 === "" });

    useEffect(() => {
        if (setNameIsError) {
            const err = setNameError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load set name data",
                    }
                });
            }
        }

        if (!setNameIsLoading && setNameData !== undefined) {
            // Add the current selected value to the list of options
            // to prevent the "no matching option" error
            const options:FieldSearchSetNameValue[] = [...setNameData];
            if (values.setNameValue !== null && !setNameData.some((item) => item.setName === values.setNameValue?.setName)) {
                options.push(values.setNameValue);
            }
            setSetNameOptions(options);
        }
    }, [setNameData, setNameIsError, setNameIsLoading, setNameError, alertDispatch, values.setNameValue])

    const getSetNameLabel = (option: FieldSearchSetNameValue) => {
        if (option.setName !== null) {
            return option.setName;
        }
        return '';
    }

    const handleSetNameChange = (event: React.SyntheticEvent, value: string | FieldSearchSetNameValue | null) => {
        setFieldValue("setNameValue", value);
    }

    const handleSetNameInputChange = (event: React.SyntheticEvent<Element, Event>, value: string, reason: string) => {
        if (reason !== "reset") {
            setSetNameSearchText(value);
        } else {
            setSetNameSearchText(""); // Reset the search list so the options aren't empty
        }
    }

    const handleSetNameAddClick = (value: string | undefined) => {
        if (value !== undefined && value !== "") {
            if (!setNameOptions.some((v) => v.setName === value)) {
                const newValue = { setName: value };
                const [...options] = setNameOptions;

                options.push(newValue);
                setSetNameOptions(options);
                setFieldValue("setNameValue", newValue);
            }
        }
    }

    const [recordSeriesOptions, setRecordSeriesOptions] = useState<FieldSearchRecordSeriesValue[]>([])
    const [recordSeriesSearchText, setRecordSeriesSearchText] = useState("");

    const { // TODO prevent initial load until user types
        data: recordSeriesData,
        error: recordSeriesError,
        isFetching: recordSeriesIsFetching,
        isError: recordSeriesIsError,
    } = useSearchRecordSeriesFieldQuery({
        recordSeries: recordSeriesSearchText,
        accountLevel1: values.accountLevel1,
        accountLevel2: values.accountLevel2,
        accountLevel3: values.accountLevel3,
        type: values.type,
    }, { skip: values.accountLevel1 === "" || values.type === "" });

    useEffect(() => {
        if (recordSeriesIsError) {
            const err = recordSeriesError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load record series data",
                    }
                });
            }
        }

        if (!recordSeriesIsFetching && recordSeriesData !== undefined) {
            // Add the current selected value to the list of options
            // to prevent the "no matching option" error
            const options:FieldSearchRecordSeriesValue[] = [...recordSeriesData];
            if (values.recordSeriesValue !== null && !recordSeriesData.some((item) => item.recordSeriesCode === values.recordSeriesValue?.recordSeriesCode)) {
                options.push(values.recordSeriesValue);
            }
            setRecordSeriesOptions(options);
        }
    }, [recordSeriesData, recordSeriesIsError, recordSeriesIsFetching, recordSeriesError, alertDispatch, values.recordSeriesValue])

    const getRecordSeriesLabel = (option: FieldSearchRecordSeriesValue) => {
        if (option.recordSeriesId !== null) {
            return `(${option.recordSeriesCode}) ${option.recordSeriesTitle}`
        }
        return '';
    }

    const handleRecordSeriesChange = (event: React.SyntheticEvent, value: string | FieldSearchRecordSeriesValue | null) => {
        if (value === null || typeof value === "string") {
            if (value !== "reset") {
                setFieldValue("destroyDate", null);
                setFieldValue("permanent", false)
                setFormSnackbar({open: true, message: "Destroy date and permanent flag re-enabled"});
            }
        } else {
            setFormSnackbar({open: true, message: "Destroy date and permanent flag will be set from record series"});
        }

        setFieldValue("recordSeriesValue", value);
    }

    const handleRecordSeriesInputChange = (event: React.SyntheticEvent<Element, Event>, value: string, reason: string) => {
        if (reason !== "reset") {
            setRecordSeriesSearchText(value);
        } else {
            setRecordSeriesSearchText(""); // Reset the search list so the options aren't empty
        }
    }

    const handlePermanentChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setFieldValue("permanent", value === "true");
    }

    // We only want this to run the first time the form loads, but disabling "react-hooks/exhaustive-deps"
    // isn't good practice as it won't warn if we introduce future dependencies.
    useEffect(() => {
        if (values.containerId !== "") {
            setContainerOptions([
                {
                    id: values.containerId,
                    barcode: values.containerBarcode,
                    altCode: values.containerAltCode,
                    accountLevel1: values.accountLevel1,
                    accountLevel2: values.accountLevel2,
                    accountLevel3: values.accountLevel3,
                }
            ])
        }

        if (values.setName !== "") {
            setSetNameOptions([{setName: values.setName}]);
        }

        if (values.recordSeriesId !== "") {
            setRecordSeriesOptions([
                {
                    recordSeriesId: values.recordSeriesId,
                    recordSeriesCode: values.recordSeriesCode,
                    recordSeriesTitle: values.recordSeriesTitle,
                    legalHold: values.legalHold,
                }
            ])
        }
    }, []);

    const { // TODO prevent initial load if record series is populated on load
        data: calcRSData,
        error: calcRSError,
        isFetching: calcRSIsFetching,
        isError: calcRSIsError,
    } = useCalculateRecordSeriesQuery({
        value: {
            recordSeriesId: values.recordSeriesValue !== null ? values.recordSeriesValue?.recordSeriesId : "",
            accountLevel1: values.accountLevel1,
            accountLevel2: values.accountLevel2,
            accountLevel3: values.accountLevel3,
            contentsStartDate: values.contentsStartDate,
            contentsEndDate: values.contentsEndDate,
            customDate: values.customDate,
            addDate: values.addDate,
        },
        context: values.id !== "" ? values.id : values.type,
    }, { skip: values.recordSeriesValue === null || values.recordSeriesValue.recordSeriesId === null });

    useEffect(() => {
        if (calcRSIsError) {
            const err = calcRSError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load calculate record series data",
                    }
                });
            }
        }

        if (!calcRSIsFetching && calcRSData !== undefined) {
            setFieldValue("destroyDate", calcRSData.destroyDate);
            setFieldValue("legalHold", calcRSData.legalHold);
        }
    }, [calcRSData, calcRSIsError, calcRSIsFetching, calcRSError, alertDispatch, setFieldValue])

    const finalFields = useMemo(():APIFormFieldDef[] => {
        const fields = [...formDefinition.fields];
        const result:APIFormFieldDef[] = [];
        let barcodeFound = false;

        fields.forEach((field) => {
            if (field.fieldId === "contentsRangeStart") {
                result.push({
                    ...field,
                    fieldId: "contentsRange",
                });
            } else if (field.fieldId === "contentsStartDate") {
                result.push({
                    ...field,
                    fieldId: "contentsDateRange",
                });
            } else if (field.fieldId === "containerBarcode" && values.type === "container") {
                // Remove the "containerBarcode" field if this is a container form,
                // replacing it with the barcode field if it doesn't already exist.
                if  (!barcodeFound) {
                    barcodeFound = true;
                    result.push({
                        ...field,
                        fieldId: "barcode",
                    });
                }
            } else if (field.fieldId === "barcode") {
                // Only add the barcode if we've not already processed it
                if  (!barcodeFound) {
                    barcodeFound = true;
                    result.push({
                        ...field,
                        fieldId: "barcode",
                    });
                }
            } else if (field.fieldId !== "containerAltCode" && field.fieldId !== "contentsRangeEnd" && field.fieldId !== "contentsEndDate") {
                // Don't include containerAltCode, contentsRangeEnd, or
                // contentsEndDate as they are handled by other fields
                result.push(field);
            }
        });

        return result;
    }, [formDefinition.fields, values.type]);

    const layout = useMemo(() => {
        const result:Array<Array<APIFormFieldDef>> = [];

        for (let i = 0; i < Math.ceil(finalFields.length / formDefinition.columns); i++) {
            result.push(finalFields.slice(i * formDefinition.columns, (i * formDefinition.columns) + formDefinition.columns));
        }

        if (formDefinition.displayOrder === "V") {
            // Transpose the layout array from horizontal to vertical
            // (inspired by https://hashinteractive.com/blog/javascript-transpose-matrix-rows-to-columns/)
            const [row] = result;
            return row.map((value, column) => result.map(row => row[column]))
        }

        return result;
    }, [finalFields, formDefinition]);

    const colWidth = useMemo(() => {
        return formDefinition.columns >= 4 ? 3 : 12 / formDefinition.columns;
    }, [formDefinition]);

    if (values.type === "file" && tempBarcodeIsFetching) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100vh"
            >
                <CircularProgress/>
            </Box>
        )
    } else {
        return (
            <form onSubmit={handleSubmit}>
                <button ref={submitButtonRef} type="submit" style={{display: "none"}}/>
                <input type="hidden" name="id" value={values.id}/>
                <Grid container>
                    {layout.map((row, rowIndex) => {
                        return (
                            <Grid item container xs={12} key={`row${rowIndex}`}>
                                {row.map((column, colIndex) => {
                                    if (column === undefined) {
                                        return (<div key={`col${colIndex}`}>{null}</div>);
                                    }
                                    if (column.type === "H") {
                                        return (
                                            <input type="hidden" key={`col${colIndex}`} name={column.fieldId} value={column.defaultValue}/>
                                        )
                                    } else if (column.fieldId === "status") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormControl sx={{ width: 150, marginRight: 1 }}>
                                                    <TextField
                                                        select
                                                        label={column.tag}
                                                        variant="standard"
                                                        size="small"
                                                        disabled={disabled || column.type === "R"}
                                                        required={Boolean(column.required)}
                                                        value={values.status}
                                                    >
                                                        <MenuItem value="in">In</MenuItem>
                                                        <MenuItem value="out">Out</MenuItem>
                                                        <MenuItem value="pending">Pending</MenuItem>
                                                        <MenuItem value="destroyed">Destroyed</MenuItem>
                                                        <MenuItem value="permout">Perm. Out</MenuItem>
                                                    </TextField>
                                                </FormControl>
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "statusDate") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="statusDate"
                                                    variant="standard"
                                                    label={column.tag}
                                                    disabled
                                                    value={statusDate}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "addDate") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="addDate"
                                                    variant="standard"
                                                    label={column.tag}
                                                    disabled
                                                    value={addDate}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "destroyDate") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        label={column.tag}
                                                        inputFormat="dd/MM/yyyy"
                                                        value={values.destroyDate}
                                                        disabled={
                                                            disabled ||
                                                            column.type === "R" ||
                                                            (values.recordSeriesValue !== null && values.recordSeriesValue?.recordSeriesId !== null)
                                                        }
                                                        onChange={(newValue: Date | null) => {
                                                            if (isValidDate(newValue)) {
                                                                setFieldValue("destroyDate", formatDateToISO(newValue));
                                                            } else {
                                                                setFieldValue("destroyDate", null);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <Box sx={{display: "flex", alignItems: "end"}}>
                                                                <TextField
                                                                    {...params}
                                                                    variant="standard"
                                                                    size="small"
                                                                    sx={{ width: "150px", marginRight: 1 }}
                                                                    error={touched.destroyDate && Boolean(errors.destroyDate)}
                                                                    helperText={touched.destroyDate && errors.destroyDate}
                                                                    required={Boolean(column.required)}
                                                                />
                                                                <Button
                                                                    disabled={
                                                                        disabled ||
                                                                        column.type === "R" ||
                                                                        (values.recordSeriesValue !== null && values.recordSeriesValue?.recordSeriesId !== null)
                                                                    }
                                                                    sx={{minWidth: "32px"}}
                                                                    title="Clear"
                                                                    onClick={() => {
                                                                        setFieldValue("destroyDate", null);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon size="lg" icon={["fas", "xmark"]}/>
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "barcode") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="barcode"
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R" ||
                                                        tempBarcodeIsFetching ||
                                                        values.id !== ""
                                                    }
                                                    required={Boolean(column.required)}
                                                    error={touched.barcode && Boolean(errors.barcode)}
                                                    helperText={touched.barcode && errors.barcode}
                                                    value={values.barcode}
                                                    onChange={handleChange}
                                                    onKeyDown={handleKeyDown}
                                                    sx={{marginTop: "3px", marginRight: 1}}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "altCode") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="altCode"
                                                    label={column.tag}
                                                    disabled={column.type === "R"}
                                                    required={Boolean(column.required)}
                                                    error={touched.altCode && Boolean(errors.altCode)}
                                                    helperText={touched.altCode && errors.altCode}
                                                    value={values.altCode}
                                                    onChange={handleChange}
                                                    sx={{marginTop: "3px", marginRight: 1}}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "containerBarcode") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <CartonPicker
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    enableOpenFile={values.type === "file"}
                                                    error={touched.containerValue && Boolean(errors.containerValue)}
                                                    helperText={touched.containerValue && errors.containerValue}
                                                    isLoading={containerIsFetching}
                                                    options={containerOptions}
                                                    getOptionLabel={getContainerLabel}
                                                    value={values.containerValue}
                                                    onInputChange={handleContainerInputChange}
                                                    onChange={handleContainerChange}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "account") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <AccountPickerGroup
                                                    type={type}
                                                    error={Boolean(errors.accountLevel1)}
                                                    helperText={errors.accountLevel1}
                                                    required={Boolean(column.required)}
                                                    label={column.tag}
                                                    value={{
                                                        level1: values.accountLevel1,
                                                        level2: values.accountLevel2,
                                                        level3: values.accountLevel3,
                                                    }}
                                                    onChange={handleAccountChange}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R" ||
                                                        (values.containerValue !== null && values.containerValue.id !== "OPENFILE")
                                                    }
                                                    isHorizontal={false}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "contentsRange") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <ContentsRange
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    error={touched.contentsRangeStart && Boolean(errors.contentsRangeStart)}
                                                    helperText={touched.contentsRangeStart && errors.contentsRangeStart}
                                                    value={{
                                                        start: values.contentsRangeStart,
                                                        end: values.contentsRangeEnd,
                                                    }}
                                                    onChange={(newValue: ContentsRangeValue) => {
                                                        setFieldValue("contentsRangeStart", newValue.start);
                                                        setFieldValue("contentsRangeEnd", newValue.end);
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "contentsDateRange") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <Grid item container sx={{border: "solid 1px rgb(0, 0, 0, 0.42)", borderRadius: "4px", padding: 1, height: "130px"}}>
                                                    <Grid item xs={12}>
                                                        <Grid container item>
                                                            <Grid item>
                                                                <Typography variant="subtitle2" sx={{textAlign: "left"}}>{column.tag}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={11}>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                            <DesktopDateRangePicker
                                                                disabled={
                                                                    disabled ||
                                                                    column.type === "R"
                                                                }
                                                                inputFormat="dd/MM/yyyy"
                                                                value={[
                                                                    parseDate(values.contentsStartDate),
                                                                    parseDate(values.contentsEndDate)
                                                                ]}
                                                                onChange={(newValue: DateRange<Date>) => {
                                                                    setFieldValue("contentsStartDate", formatDateToISO(newValue[0]));
                                                                    setFieldValue("contentsEndDate", formatDateToISO(newValue[1]));
                                                                }}
                                                                renderInput={(startProps: MuiTextFieldProps, endProps: MuiTextFieldProps) => (
                                                                    <React.Fragment>
                                                                        <TextField
                                                                            {...startProps}
                                                                            variant="standard"
                                                                            label="Start Date"
                                                                            error={
                                                                                Boolean(errors.contentsStartDate) ||
                                                                                Boolean(errors.contentsEndDate)
                                                                            }
                                                                            required={Boolean(column.required)}
                                                                        />
                                                                        <Box sx={{mx: 2}}> to </Box>
                                                                        <TextField
                                                                            {...endProps}
                                                                            variant="standard"
                                                                            label="End Date"
                                                                            error={
                                                                                Boolean(errors.contentsStartDate) ||
                                                                                Boolean(errors.contentsEndDate)
                                                                            }
                                                                            required={Boolean(column.required)}
                                                                        />
                                                                    </React.Fragment>
                                                                )}
                                                            />
                                                        </LocalizationProvider>
                                                        {Boolean(errors.contentsStartDate) &&
                                                            <Typography sx={{color: 'secondary.main'}}>{errors.contentsStartDate}</Typography>
                                                        }
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Button
                                                            disabled={
                                                                disabled ||
                                                                column.type === "R"
                                                            }
                                                            sx={{minWidth: "32px"}}
                                                            title="Clear"
                                                            onClick={() => {
                                                                setFieldValue("contentsStartDate", "");
                                                                setFieldValue("contentsEndDate", "");
                                                            }}
                                                        >
                                                            <FontAwesomeIcon size="lg" icon={["fas", "xmark"]}/>
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "setName") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <LookupPicker<FieldSearchSetNameValue>
                                                    label={column.tag}
                                                    isLoading={setNameIsLoading}
                                                    options={setNameOptions}
                                                    isOptionEqualToValue={(option: FieldSearchSetNameValue, value: FieldSearchSetNameValue) => option.setName === value.setName}
                                                    getOptionLabel={getSetNameLabel}
                                                    value={values.setNameValue}
                                                    error={touched.setNameValue && Boolean(errors.setNameValue)}
                                                    helperText={touched.setNameValue && errors.setNameValue}
                                                    onInputChange={handleSetNameInputChange}
                                                    onChange={handleSetNameChange}
                                                    onAddClick={handleSetNameAddClick}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    freeSolo
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "recordSeries") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <LookupPicker<FieldSearchRecordSeriesValue>
                                                    label={column.tag}
                                                    isLoading={recordSeriesIsFetching}
                                                    options={recordSeriesOptions}
                                                    isOptionEqualToValue={(option: FieldSearchRecordSeriesValue, value: FieldSearchRecordSeriesValue) => option.recordSeriesId === value.recordSeriesId}
                                                    getOptionLabel={getRecordSeriesLabel}
                                                    value={values.recordSeriesValue}
                                                    error={touched.recordSeriesValue && Boolean(errors.recordSeriesValue)}
                                                    helperText={touched.recordSeriesValue && errors.recordSeriesValue}
                                                    onInputChange={handleRecordSeriesInputChange}
                                                    onChange={handleRecordSeriesChange}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "customField1") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="customField1"
                                                    variant="standard"
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    error={touched.customField1 && Boolean(errors.customField1)}
                                                    helperText={touched.customField1 && errors.customField1}
                                                    value={values.customField1}
                                                    onChange={handleChange}
                                                    sx={{ width: "95%" }}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "customField2") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="customField2"
                                                    variant="standard"
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    error={touched.customField2 && Boolean(errors.customField2)}
                                                    helperText={touched.customField2 && errors.customField2}
                                                    value={values.customField2}
                                                    onChange={handleChange}
                                                    sx={{ width: "95%" }}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "customField3") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="customField3"
                                                    variant="standard"
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    error={touched.customField3 && Boolean(errors.customField3)}
                                                    helperText={touched.customField3 && errors.customField3}
                                                    value={values.customField3}
                                                    onChange={handleChange}
                                                    sx={{ width: "95%" }}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "customField4") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="customField4"
                                                    variant="standard"
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    error={touched.customField4 && Boolean(errors.customField4)}
                                                    helperText={touched.customField4 && errors.customField4}
                                                    value={values.customField4}
                                                    onChange={handleChange}
                                                    sx={{ width: "95%" }}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "customDate") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        label={column.tag}
                                                        inputFormat="dd/MM/yyyy"
                                                        value={values.customDate}
                                                        disabled={
                                                            disabled ||
                                                            column.type === "R"
                                                        }
                                                        onChange={(newValue: Date | null) => {
                                                            if (isValidDate(newValue)) {
                                                                setFieldValue("customDate", formatDateToISO(newValue));
                                                            } else {
                                                                setFieldValue("customDate", null);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <Box sx={{display: "flex", alignItems: "end"}}>
                                                                <TextField
                                                                    {...params}
                                                                    variant="standard"
                                                                    size="small"
                                                                    sx={{ width: "150px", marginRight: 1 }}
                                                                    error={touched.customDate && Boolean(errors.customDate)}
                                                                    helperText={touched.customDate && errors.customDate}
                                                                    required={Boolean(column.required)}
                                                                />
                                                                <Button
                                                                    disabled={
                                                                        disabled ||
                                                                        column.type === "R"
                                                                    }
                                                                    sx={{minWidth: "32px"}}
                                                                    title="Clear"
                                                                    onClick={() => {
                                                                        setFieldValue("customDate", null);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon size="lg" icon={["fas", "xmark"]}/>
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "shortDescription") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="shortDescription"
                                                    label={column.tag}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    sx={{width: "50%"}}
                                                    error={touched.shortDescription && Boolean(errors.shortDescription)}
                                                    helperText={touched.shortDescription && errors.shortDescription}
                                                    value={values.shortDescription}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "longDescription") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="longDescription"
                                                    label={column.tag}
                                                    multiline
                                                    rows={8}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    variant="outlined"
                                                    sx={{width: "100%", marginTop: 1}}
                                                    error={touched.longDescription && Boolean(errors.longDescription)}
                                                    helperText={touched.longDescription && errors.longDescription}
                                                    value={values.longDescription}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "contents") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormTextField
                                                    name="contents"
                                                    label={column.tag}
                                                    multiline
                                                    rows={8}
                                                    disabled={
                                                        disabled ||
                                                        column.type === "R"
                                                    }
                                                    required={Boolean(column.required)}
                                                    variant="outlined"
                                                    sx={{width: "100%", marginTop: 1}}
                                                    error={touched.contents && Boolean(errors.contents)}
                                                    helperText={touched.contents && errors.contents}
                                                    value={values.contents}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        );
                                    } else if (column.fieldId === "permanent") {
                                        return (
                                            <Grid item xs={colWidth} sx={{padding: 1}} key={`col${colIndex}`}>
                                                <FormLabel sx={{paddingRight: 3}}>{column.tag}</FormLabel>
                                                <FormControlLabel
                                                    control={
                                                      <RadioGroup
                                                          row
                                                          name="permanent"
                                                          color="secondary"
                                                          value={values.permanent}
                                                          onChange={handlePermanentChange}
                                                      >
                                                          <FormControlLabel
                                                            value={true}
                                                            control={<Radio/>}
                                                            label="yes"
                                                            disabled={
                                                                disabled ||
                                                                column.type === "R" ||
                                                                (values.recordSeriesValue !== null && values.recordSeriesValue?.recordSeriesId !== null)
                                                            }
                                                          />
                                                          <FormControlLabel
                                                            value={false}
                                                            control={<Radio/>}
                                                            label="no"
                                                            disabled={
                                                                disabled ||
                                                                column.type === "R" ||
                                                                (values.recordSeriesValue !== null && values.recordSeriesValue?.recordSeriesId !== null)
                                                            }
                                                          />
                                                        </RadioGroup>
                                                    }
                                                 label=""/>
                                            </Grid>
                                        );
                                    } else {
                                        console.warn(`Unknown fieldId [${column.fieldId}]`);
                                        return (<div key={`col${colIndex}`}/>);
                                    }
                                })}
                            </Grid>
                        );
                    })}
                </Grid>
                <Snackbar open={formSnackbar.open} autoHideDuration={6000} onClose={handleFormSnackbarClose}>
                    <Alert onClose={handleFormSnackbarClose} severity="info" sx={{ width: '100%' }}>
                        {formSnackbar.message}
                    </Alert>
                </Snackbar>
            </form>
        );
    }
}
